import { IconButtonStyledOutlined } from '@/components/common/iconButtonStyledOutlined';
import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Add from '@mui/icons-material/Add';
import EditOutlined from '@mui/icons-material/EditOutlined';
import MoreVertFilledIcon from '@/components/icons/moreVertFilled';
import { useState } from 'react';
import { PaperFrameStyled } from '@/components/common/paperFrameStyled';
import StyledSelect from '@/components/common/styledSelect';
import { stringToColor } from '@/utils/string';
import dayjs from '@/config/dayjs';

enum NoteCommentsSortBy {
  MostRecent = 'most_recent',
}

interface Props {
  open: boolean;
  toggleDrawer: (v: boolean) => void;
}
const NoteDetail = ({ open, toggleDrawer }: Props) => {
  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={() => {
        toggleDrawer(false);
      }}
      variant='temporary'
    >
      <Stack sx={{ width: '1008px' }}>
        <Stack
          p={3}
          direction='row'
          justifyContent='space-between'
          border={1}
          borderColor='rgba(0,0,0,0.12)'
        >
          <Typography variant='h5'>Note Name</Typography>

          <IconButtonStyledOutlined
            onClick={() => {
              toggleDrawer(false);
            }}
          >
            <CloseIcon />
          </IconButtonStyledOutlined>
        </Stack>

        <Content />
      </Stack>
    </Drawer>
  );
};

const sortOptions = [
  { value: NoteCommentsSortBy.MostRecent, label: 'Most recent' },
];
const Content = () => {
  const [sortBy, setSortBy] = useState<NoteCommentsSortBy>(
    NoteCommentsSortBy.MostRecent,
  );
  const handleSortByChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as NoteCommentsSortBy);
  };

  return (
    <Stack
      direction='row'
      px={3}
      py={4}
      gap={5}
    >
      <Stack gap={2}>
        <Stack direction='column'>
          <Stack
            direction='row'
            gap={0.5}
            alignItems='center'
            color='action'
          >
            <Typography variant='subtitle1'>Client</Typography>
            <EditOutlined sx={{ width: 16, height: 16 }} />
          </Stack>

          <Typography variant='body1'>Bob Smith</Typography>
        </Stack>

        <Stack direction='column'>
          <Stack
            direction='row'
            gap={0.5}
            alignItems='center'
            color='action'
          >
            <Typography variant='subtitle1'>Type</Typography>
            <EditOutlined sx={{ width: 16, height: 16 }} />
          </Stack>

          <Chip
            size='small'
            label='Client meeting'
            sx={{ fontSize: 12, fontWeight: 600 }}
            color='success'
          ></Chip>
        </Stack>

        <Stack direction='column'>
          <Stack
            direction='row'
            gap={0.5}
            alignItems='center'
            color='action'
          >
            <Typography variant='subtitle1'>Account</Typography>
            <EditOutlined sx={{ width: 16, height: 16 }} />
          </Stack>

          <Typography variant='body1'>Savings</Typography>
        </Stack>

        <Stack direction='column'>
          <Stack
            direction='row'
            gap={0.5}
            alignItems='center'
            color='action'
          >
            <Typography variant='subtitle1'>Created</Typography>
            <EditOutlined sx={{ width: 16, height: 16 }} />
          </Stack>

          <Typography variant='body1'>8/24/24</Typography>
        </Stack>

        <Stack direction='column'>
          <Stack
            direction='row'
            gap={0.5}
            alignItems='center'
            color='action'
          >
            <Typography variant='subtitle1'>Last Updated</Typography>
            <EditOutlined sx={{ width: 16, height: 16 }} />
          </Stack>

          <Typography variant='body1'>8/24/24</Typography>
        </Stack>
      </Stack>

      <Divider
        orientation='vertical'
        variant='middle'
      />

      <Stack
        gap={8}
        width='100%'
        height='full'
      >
        <Stack gap={1}>
          <Typography variant='h6'>To-Do</Typography>

          <Stack>
            <TodoItem
              title='Review client meeting notes'
              completed={false}
              due_at='8/25/24'
              description="Discuss the client's progress and make necessary adjustments."
            />

            <TodoItem
              title='Schedule another meeting'
              completed={false}
              due_at='8/26/24'
              description='Set a new meeting date and time.'
            />
          </Stack>

          <PaperFrameStyled
            sx={{
              borderRadius: '8px !important',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              startIcon={<Add />}
              sx={{ color: 'rgba(0, 0, 0, 0.56)' }}
            >
              Add To-Do
            </Button>
          </PaperFrameStyled>
        </Stack>

        <Stack
          gap={4}
          width='100%'
        >
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            flex={1}
          >
            <Typography variant='h5'>Comments</Typography>
            <StyledSelect
              label='Sort By'
              options={sortOptions}
              value={sortBy}
              onChange={handleSortByChange}
            />
          </Stack>

          <Stack gap={2}>
            <CommentItem
              item={{
                author: 'Bob Johnson',
                content:
                  'Left a meeting at Acme today and it sounds like we have a new prospect being added.',
                created_at: '8/24/24',
              }}
            />
            <CommentItem
              item={{
                author: 'Bob Johnson',
                content:
                  'Left a meeting at Acme today and it sounds like we have a new prospect being added.',
                created_at: '8/24/24',
              }}
            />
            <CommentItem
              item={{
                author: 'Bob Johnson',
                content:
                  'Left a meeting at Acme today and it sounds like we have a new prospect being added.',
                created_at: '8/24/24',
              }}
            />
          </Stack>

          <PaperFrameStyled
            sx={{
              borderRadius: '8px !important',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              startIcon={<Add />}
              sx={{ color: 'rgba(0, 0, 0, 0.56)' }}
            >
              Add New Comment
            </Button>
          </PaperFrameStyled>
        </Stack>
      </Stack>
    </Stack>
  );
};

type TodoItemProps = {
  title: string;
  completed: boolean;
  description: string;
  due_at: string;
};
const TodoItem = ({ title, completed, due_at, description }: TodoItemProps) => {
  const [done, setDone] = useState(completed);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <Stack
      direction='row'
      py={1}
      alignItems='center'
    >
      <Stack
        direction='row'
        gap={1}
        flex={1}
      >
        <Checkbox
          checked={done}
          onChange={(_, checked) => {
            setDone(checked);
          }}
        />

        <Stack sx={{ opacity: done ? 0.7 : 1 }}>
          <Typography variant='subtitle1'>{title}</Typography>
          <Typography variant='body2'>
            <b>{description}</b> by <b>{due_at}</b>
          </Typography>
        </Stack>
      </Stack>

      <IconButton
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreVertFilledIcon sx={{ height: 24, width: 24 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
      >
        <MenuItem>Rename To-Do</MenuItem>
        <MenuItem>Change Assignee</MenuItem>
        <MenuItem>Rename due date</MenuItem>
        <MenuItem sx={{ color: 'red' }}>Delete To-Do</MenuItem>
      </Menu>
    </Stack>
  );
};

type Comment = {
  author: string;
  content: string;
  created_at: string;
};
const CommentItem = ({
  item: { author, content, created_at },
}: {
  item: Comment;
}) => {
  return (
    <Stack gap={2}>
      <ListItem sx={{ px: 0 }}>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: stringToColor(author) }}>{`${
            author.split(' ')[0][0]
          }${author.split(' ')[1][0]}`}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={author}
          secondary={dayjs(created_at).format('h:mm a on M/D/YY')}
        />
      </ListItem>

      <Typography variant='body2'>{content}</Typography>
    </Stack>
  );
};

export default NoteDetail;
